import {Component, Input, SimpleChanges} from '@angular/core';
import * as $ from 'jquery';
import * as d3 from 'd3';
import {
  GroupedBarChartModel
} from '../../../../../models/grouped-bar-chart.model';
import * as uuid from 'uuid';
import moment from 'moment/moment';
import {CommonService} from '../../../services/common.service';

@Component({
  selector: 'grouped-bar-chart-two',
  templateUrl: './grouped-bar-chart-two.component.html',
  styleUrls: ['./grouped-bar-chart-two.component.css']
})
export class GroupedBarChartTwoComponent {
  chartId = 'x' + uuid.v4();
  chatIdSelector = '#' + this.chartId;

  @Input() chartName: string;
  @Input() chartData: GroupedBarChartModel;
  @Input() showTargetLine: boolean;
  @Input() yAxisLabel: string;
  @Input() xAxisLabel: string;

  svg: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  x: d3.ScaleBand<string>;
  y: d3.ScaleLinear<number, number, never>;
  width: number;
  height: number;
  resizeObserver: ResizeObserver;
  originalMargins = {top: 2, right: 2, bottom: 5.5, left: 3.5};
  margins = {top: 2, right: 2, bottom: 5.5, left: 3.5};

  constructor(private commonService: CommonService) {
    // creating resize observer for detecting size change of chart container
    this.resizeObserver = new ResizeObserver(() => {
      this.renderChart();
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['chartData']?.currentValue) {
      this.renderChart();
    }
  }

  ngOnInit() {
    setTimeout(() => {
      const chartContainer = document.getElementById(this.chartId);
      this.resizeObserver.observe(chartContainer);
    });
  }

  renderChart() {
    if (this.chartData) {
      setTimeout(() => {
        this.createChart();
      })
    }
  }

  createChart() {
    this.width = $(this.chatIdSelector).width();
    this.height = $(this.chatIdSelector).height();
    this.margins.top = this.commonService.convertRemToPx(this.originalMargins.top);
    this.margins.right = this.commonService.convertRemToPx(this.originalMargins.right);
    this.margins.left = this.commonService.convertRemToPx(this.originalMargins.left);
    this.margins.bottom = this.commonService.convertRemToPx(this.originalMargins.bottom);

    const element = document.getElementById(this.chartId);
    if (!element) {
      // component destroyed but ngOnDestroy event is not triggered, so manually triggering
      // NOTE: This is happening when switching between XS <=> SM
      this.ngOnDestroy();
      return
    }
    element.innerHTML = "";

    // ********** DRAWING THE SVG ON SCREEN & ADJUSTING OR SETTING VIEW-BOX WIDTH AND HEIGHT **********//
    this.svg = d3.select(this.chatIdSelector)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0, 0, ${this.width}, ${this.height}`)
      .attr('preserveAspectRatio', 'xMinYMin')
      // .call(d3.zoom().on("zoom", (e) => {
      //   svg.attr("transform", e.transform)
      // }))
      .append('g')
      .attr('class', 'main-content')

    // prepare chart data from given inputs
    // const chartData = this.getChartData();
    // console.log(chartData);

    // List of subgroups = header of the csv files = soil condition here
    // const subgroups = chartData.groups;
    const subgroups = this.chartData.data[0].data.map((value, index) => 'subgroup_' + index);

    // List of groups = species here = value of the first column called group -> I show them on the X axis
    const groups = this.chartData.data.map((item) => item.title);

    // Add X axis
    this.x = d3.scaleBand()
      .domain(groups)
      .range([this.margins.left, this.width - this.margins.right])
      .padding(0.2)
    // Draw X axis
    const axisBottom = this.svg.append('g')
      .attr('transform', `translate(0,${this.height - this.margins.bottom})`)
      .call(d3.axisBottom(this.x).tickSize(0).tickFormat((title) => {
        const foundItem = this.chartData.data.find((item) => item.title === title);
        if (foundItem) {
          const maxTick = Math.ceil(this.width / 60);
          if (this.chartData.data.length > maxTick) {
            return (foundItem.smallTitle || title);
          }
          return title;
        }
        return title;
      }));

    // X Axis label
    axisBottom.selectAll('text')
      .attr('dy', '1em')
      .attr('class', 'x-axis-tick-title')

    this.svg.append("text")
      .attr('class', 'x-axis-label')
      .attr("x", this.width / 2)
      .attr("y", this.height)
      .style("text-anchor", "middle")
      .text(this.xAxisLabel);

    // X Axis subtitle
    axisBottom.selectAll('.tick')
      .append('text')
      .attr('dy', '3em')
      .attr('fill', 'currentColor')
      .attr('class', 'x-axis-tick-subtitle')
      .html((e) => {
        const maxTick = Math.ceil(this.width / 60);
        if (this.chartData.data.length > maxTick) {
          return '';
        }
        // if ((this.width < 650 && this.chartData.data.length > 14) || (this.width < 550 && this.chartData.data.length > 12) || (this.width < 550 && this.chartData.data.length > 10) || (this.width < 450 && this.chartData.data.length > 8) || (this.width < 380 && this.chartData.data.length > 6)) {
        //   return '';
        // }
        const subtitle = this.chartData.data.find((item) => item.title === e)?.subtitle;
        if (subtitle) {
          const parts = subtitle.split('-');
          if (parts.length == 2) {
            return `
            <tspan x="0" y="0em">${parts[0]}</tspan>
            <tspan x="0" y="4em">- ${parts[1]}</tspan>
            `
          }
          return subtitle;
        }
        return '';
      })

    // Add Y axis
    this.y = d3.scaleLinear()
      .domain(this.getYAxisDomain())
      .range([this.height - this.margins.bottom, this.margins.top])
      .nice();

    // Draw Y axis
    const axisLeft = this.svg.append('g')
      .attr('transform', `translate(${this.margins.left},0)`)
      .call(d3.axisLeft(this.y).ticks(7));

    // Y axis label
    axisLeft.selectAll('text')
      .attr('class', 'y-axis-label')

    this.svg.append("text")
      .attr('class', 'y-label')
      .attr("transform", "rotate(-90)")
      .attr("y", 0)
      .attr("x", 0 - (this.height / 2))
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text(this.yAxisLabel);

    // ********** Create tooltip ********** //
    const tooltip = d3.select(this.chatIdSelector)
      .append("div")
      .attr('class', 'tooltip')
      .style("position", "fixed")
      .style("visibility", 'hidden')

    // Another scale for subgroup position?
    const xSubgroup = d3.scaleBand()
      .domain(subgroups)
      .range([0, this.x.bandwidth()])
      .padding(0.05)

    // color palette = one color per subgroup
    const color = d3.scaleOrdinal<string>()
      .domain(['0', '1'])
      .range([this.chartData.config.colors.value])

    // Show the bars
    this.svg.append('g')
      .selectAll('g')
      // Enter in data = loop group per group
      .data(this.chartData.data)
      .join('g')
      .attr('class', 'grouped-bar')
      .attr('transform', (d) => `translate(${this.x(d.title)}, 0)`)
      .selectAll('rect')
      .data((d) => {
        return d.data.map((value, index) => {
          return {key: 'subgroup_' + index, value: value.points, color: value.color || (value.imputed ? this.chartData.config.colors.imputedValue : this.chartData.config.colors.value), xAxis: d.weekNumber};
        });
      })
      .join('rect')
      .attr('x', d => xSubgroup(d.key))
      .attr('y', (d) => this.y(d.value))
      .attr('width', xSubgroup.bandwidth())
      .attr('height', (d) => (this.height - this.margins.bottom) - this.y(d.value))
      .attr('fill', (d) => d.color || color(d.key))
      .on("mouseover", (event: any, d) => {
        return tooltip
          .html(`
            Week ${d.xAxis}<br>
            ${d.value} days
          `)
          .transition()
          .duration(200)
          .style('top', (event.clientY - 60) + 'px')
          .style('left', this.commonService.calculateXForPopup(tooltip, event.clientX) + 'px')
          .style("visibility", 'visible');

      })
      .on("mousemove", (event: any, d) => {
        return tooltip
          .transition()
          .duration(0)
          .style('top', (event.clientY - 60) + 'px')
          .style('left', this.commonService.calculateXForPopup(tooltip, event.clientX) + 'px');
      })
      .on("mouseout", () => {
        return tooltip
          .transition()
          .duration(0)
          .style("visibility", 'hidden');
      });

    if (this.showTargetLine) {
      this.toggleTargetLine();
    }
  }

  toggleTargetLine() {
    this.svg.selectAll('.target-line')
      .call((e) => {
        if (e.nodes().length) {
          // already drawn lines
          if (d3.select(`.main-content`).attr('show_target_line') === '0') {
            // Set target line display flag
            d3.select(`.main-content`).attr('show_target_line', '1')
            // hide target line
            d3.selectAll(`.target-line`).style('display', 'block')
          } else {
            // Set target line display flag
            d3.select(`.main-content`).attr('show_target_line', '0')
            // hide target line
            d3.selectAll(`.target-line`).style('display', 'none')
          }
        } else {
          // Draw target line for the first time
          this.svg.selectAll('.grouped-bar')
            .data(this.chartData.data)
            .filter((item) => !!item.target)
            .append('line')
            .attr('class', 'target-line')
            .attr('x1', 0)
            .attr('x2', this.x.bandwidth())
            .attr('y1', (d) => this.y(d.target))
            .attr('y2', (d) => this.y(d.target))
            .style('stroke-width', '3')
            .style('stroke', (d) => this.chartData.config.colors.targetColor);

          // Set target line display flag
          d3.select(`.main-content`).attr('show_target_line', '1')
        }
      })
  }

  getYAxisDomain() {
    // collect all Y values
    const yValues = [];
    this.chartData.data.forEach((group) => {
      yValues.push(...(group.data.map(item => item.points)));
      if (group.target) {
        yValues.push(group.target);
      }
    });
    let min = d3.min(yValues, (item) => item);
    let max = d3.max(yValues, (item) => item);
    // if (this.chartData.config.y.padding?.size) {
    //   // check min is falling below 0
    //   if (min - this.chartData.config.y.padding?.size >= 0) {
    //     min -= this.chartData.config.y.padding?.size;
    //   } else {
    //     min = 0;
    //   }
    //   max += this.chartData.config.y.padding?.size;
    // }

    max += max * 0.1;

    if (['journaling', 'screen-time'].includes(this.chartName)) {
      min = 0;
    }
    if (this.chartName === 'journaling') {
      max = 7;
    }

    return [min, max];
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }
}
